import { DYNAMIC_HOTEL_IMAGE_SIZE_LABEL } from '@constants/constants';

export enum ImageProviderId {
  SeturLocal = 0,
  MediaNova = 1,
  AzureCDN = 2,
  Cloudinary = 3,
  TatilBudur = 4,
  Expedia = 5,
  Akamai = 6,
}

// Birebir setur referansından kopyalanmıştır — gsf'ye özel hostname eklenmez (K2)
export const MEDIANOVA_CDN_HOSTNAMES: ReadonlySet<string> = new Set([
  'cdn1.mncdn.com',
  'cdn2.mncdn.com',
  'cdn3.mncdn.com',
  'cdn4.mncdn.com',
  'img.mncdn.com',
  'media.mncdn.com',
  'img-setur.sm.mncdn.com',
  'setur-stage.sm.mncdn.com',
  'setur.sm.mncdn.com',
]);

const CLOUDINARY_CDN_HOSTNAMES: ReadonlySet<string> = new Set([
  'res.cloudinary.com',
  'seturcdn-res.cloudinary.com',
]);

export const MEDIANOVA_PLACEHOLDER = '{placeholder}';

export const isMediaNovaUrl = (url: string): boolean => {
  try {
    const { hostname } = new URL(url);
    return MEDIANOVA_CDN_HOSTNAMES.has(hostname);
  } catch {
    return false;
  }
};

const isCloudinaryUrl = (url: string): boolean => {
  try {
    const { hostname } = new URL(url);
    return CLOUDINARY_CDN_HOSTNAMES.has(hostname);
  } catch {
    return false;
  }
};

export const encodeImageUrl = (url: string): string => {
  try {
    return new URL(url).toString();
  } catch {
    return url;
  }
};

const hasMediaNovaTransformation = (url: string): boolean => {
  try {
    return /\/(?:mnresize|mncropresize)\/[^/]+\/[^/]+\//.test(
      new URL(url).pathname
    );
  } catch {
    return false;
  }
};

export const medianovaResizeImage = (
  url: string,
  width?: number | string,
  height?: number | string
): string => {
  if (!url) return url;
  const w = width ?? '-';
  const h = height ?? '-';

  if (url.includes(MEDIANOVA_PLACEHOLDER)) {
    return url.replace(MEDIANOVA_PLACEHOLDER, `mnresize/${w}/${h}`);
  }

  // legacy POC format: {size} token
  if (url.includes(DYNAMIC_HOTEL_IMAGE_SIZE_LABEL)) {
    return url.replace(DYNAMIC_HOTEL_IMAGE_SIZE_LABEL, `mnresize/${w}/${h}`);
  }

  if (hasMediaNovaTransformation(url)) return encodeImageUrl(url);

  try {
    const u = new URL(url);
    u.pathname = `/mnresize/${w}/${h}${u.pathname}`;
    return encodeImageUrl(u.toString());
  } catch {
    return url;
  }
};

export const medianovaFitAndFillImage = (
  url: string,
  width?: number | string,
  height?: number | string
): string => {
  if (!url) return url;
  const w = width ?? '-';
  const h = height ?? '-';

  if (url.includes(MEDIANOVA_PLACEHOLDER)) {
    return url.replace(MEDIANOVA_PLACEHOLDER, `mncropresize/${w}/${h}`);
  }

  // legacy POC format: {size} token
  if (url.includes(DYNAMIC_HOTEL_IMAGE_SIZE_LABEL)) {
    return url.replace(
      DYNAMIC_HOTEL_IMAGE_SIZE_LABEL,
      `mncropresize/${w}/${h}`
    );
  }

  if (hasMediaNovaTransformation(url)) return encodeImageUrl(url);

  try {
    const u = new URL(url);
    u.pathname = `/mncropresize/${w}/${h}${u.pathname}`;
    return encodeImageUrl(u.toString());
  } catch {
    return url;
  }
};

export const cloudinaryResizeImage = (
  url: string,
  width?: number | string,
  height?: number | string,
  format = 'auto',
  quality = 'auto'
): string => {
  // BE may send {placeholder} instead of {size} for Cloudinary URLs
  const token = url?.includes(DYNAMIC_HOTEL_IMAGE_SIZE_LABEL)
    ? DYNAMIC_HOTEL_IMAGE_SIZE_LABEL
    : url?.includes(MEDIANOVA_PLACEHOLDER)
    ? MEDIANOVA_PLACEHOLDER
    : null;

  if (!token) return url;

  const isWidthAuto = String(width).toLowerCase() === 'auto';
  const isHeightAuto = String(height).toLowerCase() === 'auto';

  if (isWidthAuto && isHeightAuto) {
    return url.replace(
      token,
      `q_auto/w_auto/q_${quality}:best,f_${format},dpr_auto,e_sharpen:100`
    );
  }

  return url.replace(
    token,
    `${
      height ? `h_${height},` : ''
    }w_${width},q_${quality}:best,f_${format},dpr_auto,e_sharpen:100`
  );
};

export const resolveImageProvider = (
  url: string,
  providerId?: number | null
): ImageProviderId => {
  if (providerId != null) return providerId as ImageProviderId;
  if (url && isMediaNovaUrl(url)) return ImageProviderId.MediaNova;
  if (url && isCloudinaryUrl(url)) return ImageProviderId.Cloudinary;
  if (
    url?.includes(DYNAMIC_HOTEL_IMAGE_SIZE_LABEL) ||
    url?.includes(MEDIANOVA_PLACEHOLDER)
  )
    return ImageProviderId.Cloudinary;

  return ImageProviderId.SeturLocal;
};

export interface OptimizeImageOptions {
  providerId?: number | null;
  placeholderUrl?: string | null;
  format?: string;
  quality?: string;
  transformType?: 'resize' | 'fitAndFill';
}

export const optimizeImage = (
  url: string,
  width?: number | string,
  height?: number | string,
  options: OptimizeImageOptions = {}
): string => {
  if (!url) return '';

  const {
    providerId,
    placeholderUrl,
    format = 'auto',
    quality = 'auto',
    transformType = 'fitAndFill',
  } = options;

  const provider = resolveImageProvider(url, providerId);
  // Transform templates ({placeholder}/{size}) are only valid for MediaNova/Cloudinary.
  // Listing Azure/Expedia URLs often ship a leftover placeholderUrl; using it here
  // returned '' and the card fell back to "Temsili görseldir".
  const canTransform =
    provider === ImageProviderId.MediaNova ||
    provider === ImageProviderId.Cloudinary;
  const sourceUrl = canTransform ? placeholderUrl || url : url;

  if (provider === ImageProviderId.MediaNova) {
    return transformType === 'resize'
      ? medianovaResizeImage(sourceUrl, width, height)
      : medianovaFitAndFillImage(sourceUrl, width, height);
  }

  if (provider === ImageProviderId.Cloudinary) {
    if (!width && !height) return '';
    return cloudinaryResizeImage(sourceUrl, width, height, format, quality);
  }

  return url.includes(MEDIANOVA_PLACEHOLDER) ||
    url.includes(DYNAMIC_HOTEL_IMAGE_SIZE_LABEL)
    ? ''
    : encodeImageUrl(url);
};

// Sepet / liste kartları için: transform sağlayıcılarında placeholderUrl > url
export const resolveCardImageUrl = (
  url: string | null | undefined,
  placeholderUrl: string | null | undefined,
  providerId?: number | null,
  width?: number | string,
  height?: number | string,
  transformType: OptimizeImageOptions['transformType'] = 'fitAndFill'
): string => {
  const detectedUrl = url || placeholderUrl || '';
  const provider = resolveImageProvider(detectedUrl, providerId);
  const canTransform =
    provider === ImageProviderId.MediaNova ||
    provider === ImageProviderId.Cloudinary;
  const sourceUrl =
    (canTransform ? placeholderUrl || url : url || placeholderUrl) || '';
  if (!sourceUrl) return '';
  return optimizeImage(sourceUrl, width, height, {
    providerId,
    placeholderUrl: canTransform ? placeholderUrl : undefined,
    transformType,
  });
};
